import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Zap, PaintBucket, Layers, ArrowRight, Star, Phone, 
  CheckCircle, Clock, Shield, Award, ChevronRight, Quote, Users, X, ExternalLink
} from 'lucide-react';
import Footer from '../components/Footer';

// Logo et images
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_aldroubi-luxury/artifacts/t04kcxdw_Adobe%20Express%20-%20file-modified.png';
const COVER_IMAGE = 'https://customer-assets.emergentagent.com/job_aldroubi-luxury/artifacts/edx99km4_photo%20couverture.png';

// Image de fond Hero - intérieur rénové luxueux
const HERO_IMAGE = 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=2400&q=85&auto=format&fit=crop';

// Images avant/après
const IMAGES = {
  parquet_avant1: 'https://customer-assets.emergentagent.com/job_aldroubi-luxury/artifacts/l8fn6yjc_IMG_20220820_162918.jpg',
  parquet_apres1: 'https://customer-assets.emergentagent.com/job_aldroubi-luxury/artifacts/537j8m6a_IMG_20220820_164728.jpg',
  sdb_avant1: 'https://customer-assets.emergentagent.com/job_aldroubi-luxury/artifacts/gyeid9f5_Snapchat-926234349.jpg',
  sdb_apres1: 'https://customer-assets.emergentagent.com/job_aldroubi-luxury/artifacts/bv8di3wb_Snapchat-1444918410.jpg',
};

// Lien Google Avis (partagé fourni)
const GOOGLE_REVIEWS_URL = 'https://share.google/1iGXkxHbWGQy26hRR';

const HomePage = () => {
  const [hoveredProject, setHoveredProject] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  const services = [
    {
      icon: Zap,
      title: 'Électricité',
      description: 'Dépannage, installation et rénovation électrique à Argentan. Mise aux normes.',
      image: '/images/apres tableau electrique.jpeg',
    },
    {
      icon: PaintBucket,
      title: 'Peinture',
      description: 'Peinture intérieure et extérieure à Argentan et environs. Finitions soignées et durables.',
      image: '/images/peinture-escalier-apres.jpeg',
    },
    {
      icon: Layers,
      title: 'Revêtements',
      description: 'Pose de parquet, carrelage et revêtements de sols pour maisons et appartements.',
      image: IMAGES.parquet_apres1,
    },
  ];

  const features = [
    { icon: Clock, title: 'Disponible 24h/24', desc: 'Urgences jour et nuit' },
    { icon: Shield, title: 'Garantie', desc: 'Travaux assurés' },
    { icon: Award, title: '26 ans d\'expérience', desc: 'Expertise reconnue' },
    { icon: CheckCircle, title: 'Devis Gratuit', desc: 'Sans engagement' },
  ];

  // Avis Google - avec lien vers la fiche
  const reviews = [
    {
      id: '1',
      author: 'Quentin Poussier',
      rating: 5,
      comment: "Un grand merci à Mr Aldroubi. De vrai professionnel avec un respect des délais. Un super travail de A a Z. Une rénovation totale pour un prix raisonnable. Je recommande vivement. Quentin et Cassie",
    },
    {
      id: '2',
      author: 'Nicolas André',
      rating: 5,
      comment: "Chantier d'électricité et de peinture terminé dans les délais, le travail est très soigné et réalisé dans les règles de l'art. Qui plus est Mr ALDROUBI fait preuve d'un grand professionnalisme en prévenant systématiquement des dates de début et de fin de d'intervention. C'est très rare pour ne pas être souligné. Je conseille vivement.",
    },
    {
      id: '3',
      author: 'Vanessa Allard',
      rating: 5,
      comment: "Une rénovation complète de peinture et d’électricité. Monsieurs Aldroubi a fait son travaille en temps et en heure. Je recommande vivement!",
    },
    {
      id: '4',
      author: 'Pierre M.',
      rating: 5,
      comment: "Intervention rapide pour une panne électrique. Travail impeccable et tarif très correct. Merci beaucoup !",
    },
  ];

  const projects = [
    {
      id: '1',
      title: 'Pose Parquet Point de Hongrie',
      description: 'Installation de parquet chevron dans un salon & Ragreage.',
      service_type: 'Revetement de sols',
      location: 'Argentan',
      surface: '70 m²',
      date: 'Août 2022',
      before: '/images/avant-escalier-trun.jpeg',
      after: IMAGES.parquet_apres1,
    },
    {
      id: '2',
      title: 'Rénovation Salon & Escalier',
      description: 'Rénovation complète : électricité, peinture et éclairage.',
      service_type: 'peinture',
      location: 'Caen',
      surface: '50 m²',
      date: '2026',
      before: '/images/peinture-escalier-avant.jpeg',
      after: '/images/peinture-escalier-apres.jpeg',
    },
    {
      id: 3,
      title: 'Rénovation MFR Argentan',
      description: 'Avant/Après d’une rénovation à la MFR d’Argentan : sol, peinture et finitions.',
      before: '/images/MFR - Argentan/Avant-MFR/Snapchat-703911662.jpg',
      after: '/images/MFR - Argentan/Apres-MFR/Snapchat-132001294.jpg',
      type: 'Électricité & Peinture'
    },
  ];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setSelectedProject(null);
    };
    if (selectedProject) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedProject]);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Lightbox Modal (harmonisée avec GalleryPage) */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4"
            onClick={() => setSelectedProject(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="relative max-w-6xl w-full max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button (visible and accessible) */}
              <button
                onClick={() => setSelectedProject(null)}
                aria-label="Fermer"
                className="absolute top-4 right-4 z-30 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* APRÈS then AVANT */}
                <motion.div
                  className="relative rounded-lg overflow-hidden"
                  initial={{ x: 50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.1 }}
                >
                  <img src={selectedProject.after} alt="Après" className="w-full h-auto" />
                  <div className="absolute top-4 left-4 bg-green-500 text-white px-4 py-2 rounded font-bold text-lg">
                    APRÈS
                  </div>
                </motion.div>

                <motion.div
                  className="relative rounded-lg overflow-hidden"
                  initial={{ x: -50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <img src={selectedProject.before} alt="Avant" className="w-full h-auto" />
                  <div className="absolute top-4 left-4 bg-red-500 text-white px-4 py-2 rounded font-bold text-lg">
                    AVANT
                  </div>
                </motion.div>
              </div>

              <motion.div
                className="mt-6 text-center"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <h3 className="font-serif text-2xl sm:text-3xl text-white">{selectedProject.title}</h3>
                <p className="text-gray-400 mt-2">{selectedProject.description}</p>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section avec Image */}
      <section className="hero-section relative min-h-screen flex items-center overflow-hidden">
        {/* Image Background */}
        <img
          src={HERO_IMAGE}
          alt="Intérieur rénové"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40" />
        
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="luxury-label">Électricien • Peintre • Solier</span>
              <span className="bg-green-500/20 text-green-400 text-xs px-3 py-1.5 rounded-full flex items-center gap-1 font-medium">
                <Clock className="w-3 h-3" /> Disponible 24h/24
              </span>
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6">
              <span className="text-gold">ALDROUBI</span> Brico<br />
              <span className="text-gray-300">Votre Artisan de Confiance</span>
            </h1>
            <p className="text-gray-300 text-sm sm:text-base md:text-lg leading-relaxed mb-8 max-w-xl">
              Dépannage électrique, rénovation et installation. Intervention rapide sur Argentan, Caen et dans toute la Normandie.
            </p>
            <p className="text-gray-300 text-sm sm:text-base md:text-lg leading-relaxed mb-8 max-w-xl">
              ALDROUBI Brico : votre artisan électricien, peintre et solier à Argentan. Dépannage électrique, peinture intérieure/extérieure et pose de parquet soigné.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/nos-tarifs" className="btn-gold flex items-center justify-center gap-2" data-testid="hero-cta-devis">
                Devis Gratuit
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a 
                href="tel:0618709889" 
                className="btn-gold-outline flex items-center justify-center gap-2"
                data-testid="hero-cta-phone"
              >
                <Phone className="w-4 h-4" />
                06 18 70 98 89
              </a>
            </div>
          </motion.div>
        </div>

       
      </section>

      {/* Features Bar */}
      <section className="bg-[#0A0A0A] border-y border-gold/20 py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-3 sm:gap-4"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gold/10 rounded flex items-center justify-center shrink-0">
                  <feature.icon className="w-5 h-5 sm:w-6 sm:h-6 text-gold" />
                </div>
                <div>
                  <p className="text-white text-xs sm:text-sm font-medium">{feature.title}</p>
                  <p className="text-gray-500 text-xs hidden sm:block">{feature.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center">
            <span className="luxury-label">Électricien Argentan</span>
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-white mt-4">
              ALDROUBI Brico — Électricien, Peintre et Solier à Argentan
            </h2>
            <p className="text-gray-400 mt-4 max-w-3xl mx-auto text-sm sm:text-base leading-relaxed">
              Dépannage électrique, rénovation et installation rapide sur Argentan, Caen et dans toute la Normandie.
              Peinture intérieure/extérieure, pose de parquet, carrelage et revêtements de sols avec finitions soignées.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/nos-tarifs" className="btn-gold">
                Voir les tarifs
              </Link>
              <a href="tel:0618709889" className="btn-gold-outline">
                06 18 70 98 89
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Présentation Section */}
      <section className="py-16 sm:py-24 bg-[#050505]" data-testid="presentation-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Photo couverture */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="relative"
            >
              <div className="rounded-lg overflow-hidden shadow-2xl">
                <img 
                  src={COVER_IMAGE} 
                  alt="Aldroubi Brico - Électricité Générale" 
                  className="w-full h-auto"
                />
              </div>
            </motion.div>

            {/* Text */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <span className="luxury-label flex items-center gap-2">
                <Users className="w-4 h-4" />
                Électricien à Argentan
              </span>
              <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-white mt-4 mb-6">
                Présentation de <span className="text-gold">l'Entreprise</span>
              </h2>
              
              <div className="space-y-4 text-gray-300 text-sm sm:text-base leading-relaxed">
                <p>
                  L'entreprise <span className="text-gold font-medium">ALDROUBI Brico</span> réalise du dépannage électrique, 
                  des travaux d'électricité générale, de la peinture et des revêtements de sols à Argentan, Caen et dans toute la Normandie.
                  Installation de prises, interrupteurs, tableaux électriques, disjoncteurs, chauffages et éclairage.
                </p>
                <p>
                  Basé à Argentan, nous intervenons sur Argentan, Caen, Flers, Alençon et dans toute la Normandie.
                  Notre priorité est la satisfaction client et la qualité de finition.
                </p>
                <p>
                  Nous assurons également la <span className="text-white font-medium">mise aux normes</span> des installations 
                  électriques et réalisons des travaux de peinture et de revêtements de sols.
                </p>
                <p>
                  Dépannage électrique, rénovation, peinture intérieure/extérieure, pose de parquet, carrelage et sols techniques.
                </p>
              </div>

              <div className="mt-8 p-6 bg-gold/10 border border-gold/30 rounded-lg">
                <p className="text-gold font-serif text-lg mb-2">26 ans d'expérience</p>
                <p className="text-gray-400 text-sm">
                  Fort de 26 ans d'expérience, je suis attentif à votre confort et satisfaction. Interventions rapides pour 
                  vos besoins de dépannage.

                  Aldroubi Saad
                </p>
              </div>

              <div className="mt-6 flex flex-wrap gap-4">
                <Link to="/nos-tarifs" className="btn-gold">
                  Nos Tarifs
                </Link>
                <Link to="/contact" className="btn-gold-outline">
                  Nous Contacter
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 sm:py-24 bg-[#0A0A0A]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12 sm:mb-16">
            <span className="luxury-label">Nos Expertises</span>
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-white mt-4">
              Services de <span className="text-gold">Qualité Premium</span>
            </h2>
            <div className="gold-divider mx-auto mt-4" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.15 }}
                className="service-card group rounded overflow-hidden"
              >
                <div className="h-48 sm:h-64 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
                <div className="service-card-content p-4 sm:p-6">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gold/10 rounded flex items-center justify-center mb-4 group-hover:bg-gold/20 transition-colors">
                    <service.icon className="w-6 h-6 sm:w-7 sm:h-7 text-gold" />
                  </div>
                  <h3 className="font-serif text-lg sm:text-xl text-white mb-2">{service.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed mb-4">{service.description}</p>
                  <Link 
                    to="/nos-tarifs" 
                    className="inline-flex items-center gap-2 text-gold text-sm font-medium hover:gap-3 transition-all"
                  >
                    Voir les tarifs
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Gallery Section */}
      <section className="py-16 sm:py-24 bg-[#050505]" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">

          {/* Harmonisation avec GalleryPage.jsx */}
          <div className="text-center mb-12 sm:mb-16">
            <span className="luxury-label">Nos Réalisations</span>
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-white mt-4">
              Nos <span className="text-gold">Réalisations</span>
            </h2>
            <p className="text-gray-400 mt-4 text-sm sm:text-base">
              Survolez pour voir l'état avant • <span className="text-gold">Cliquez pour agrandir</span>
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {projects.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02, y: -5 }}
                whileTap={{ scale: 0.98 }}
                className="group relative cursor-pointer"
                onMouseEnter={() => setHoveredProject(item.id)}
                onMouseLeave={() => setHoveredProject(null)}
                onClick={() => setSelectedProject(item)}
                data-testid={`gallery-item-${item.id}`}
              >
                <div className="luxury-card overflow-hidden">
                  <div className="relative aspect-video sm:aspect-[4/3] overflow-hidden">
                    <motion.img
                      src={hoveredProject === item.id ? item.before : item.after}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      animate={{ scale: hoveredProject === item.id ? 1.1 : 1 }}
                      transition={{ duration: 0.5 }}
                    />
                    {/* Overlay (moins intense sur mobile) */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent sm:from-black/80 sm:via-black/20" />
                    {/* Badge AVANT/APRÈS */}
                    <motion.div 
                      className={`absolute sm:top-3 top-auto bottom-3 sm:bottom-auto left-3 sm:left-4 px-3 sm:px-4 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-bold flex items-center gap-2 ${
                        hoveredProject === item.id ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
                      }`}
                      animate={{ scale: hoveredProject === item.id ? [1, 1.1, 1] : 1 }}
                    >
                      {hoveredProject === item.id ? 'AVANT' : 'APRÈS'}
                    </motion.div>
                    {/* Service Type Badge (optionnel) */}
                    {item.service_type && (
                      <div className="absolute sm:top-3 top-auto bottom-3 sm:bottom-auto right-3 sm:right-4 px-2 sm:px-3 py-1 rounded text-xs font-medium uppercase bg-gold text-black">
                        {item.service_type}
                      </div>
                    )}
                    {/* Click indicator */}
                    <div className="absolute inset-0 hidden sm:flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <motion.div 
                        className="bg-gold/90 rounded-full p-3"
                        initial={{ scale: 0 }}
                        whileHover={{ scale: 1 }}
                        animate={{ scale: hoveredProject === item.id ? 1 : 0 }}
                      >
                        <span className="text-black font-bold text-sm">VOIR</span>
                      </motion.div>
                    </div>
                  </div>
                  {/* Content */}
                  <div className="p-4 sm:p-6">
                    <h3 className="font-serif text-base sm:text-xl text-white mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-xs sm:text-sm mb-4">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8 sm:mt-12">
            <Link to="/realisations" className="btn-gold-outline inline-flex items-center gap-2">
              Voir toutes nos réalisations
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="py-16 sm:py-24 bg-[#0A0A0A]" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12 sm:mb-16">
            <span className="luxury-label">Avis Google</span>
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-white mt-4">
              Ce Que Disent <span className="text-gold">Nos Clients</span>
            </h2>
            <div className="flex items-center justify-center gap-2 mt-4">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 text-gold fill-gold" />
                ))}
              </div>
              <span className="text-white font-medium">5.0</span>
              <span className="text-gray-400 text-sm">sur Google</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {reviews.map((review, index) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="luxury-card p-6 sm:p-8 relative"
              >
                <Quote className="absolute top-4 right-4 w-6 h-6 sm:w-8 sm:h-8 text-gold/20" />
                
                <div className="flex gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 text-gold fill-gold" />
                  ))}
                </div>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed mb-6">"{review.comment}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gold/20 rounded-full flex items-center justify-center">
                    <span className="text-gold font-medium">{review.author[0]}</span>
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm sm:text-base">{review.author}</p>
                    <p className="text-gray-500 text-xs sm:text-sm">Avis Google</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Lien vers Google Avis */}
          <div className="text-center mt-8">
            <a 
              href={GOOGLE_REVIEWS_URL}
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-gold hover:text-white transition-colors text-sm sm:text-base"
            >
              <span>Voir tous les avis sur Google</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 bg-[#050505] border-t border-gold/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-white mb-6">
            Besoin d'un <span className="text-gold">Électricien ?</span>
          </h2>
          <p className="text-gray-400 mb-4 text-sm sm:text-base">
            Intervention rapide sur Argentan, Caen et dans toute la Normandie.
          </p>
          <div className="flex items-center justify-center gap-2 text-green-400 text-sm mb-8">
            <Clock className="w-4 h-4" />
            Disponible 24h/24 - 7j/7
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/nos-tarifs" className="btn-gold">
              Devis Gratuit
            </Link>
            <a href="tel:0618709889" className="btn-gold-outline flex items-center justify-center gap-2">
              <Phone className="w-4 h-4" />
              06 18 70 98 89
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
